import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import {
  OrchestrationSlot,
  OrchestrationStepState,
} from "../../../../types/orchestration";
import {
  DEFAULT_LAYOUT,
  formatDuration,
  formatElapsed,
  formatSlotLabel,
  layoutGraph,
} from "../../../../utils/orchestrationLayout";
import { copyToClipboard } from "../../../../utils/clipboard";
import {
  SLOT_BIG_CLASS,
  SLOT_PILL_CLASS,
  SLOT_STATUS_LABEL,
} from "./orchestrationStyles";
import StepNode from "./StepNode";
import StepPopover from "./StepPopover";
import StepStatusIcon from "./StepStatusIcon";

interface SlotDagProps {
  slot: OrchestrationSlot;
  isLatest: boolean;
  nowMs: number;
}

interface TooltipState {
  step: OrchestrationStepState;
  x: number;
  y: number;
}
interface PinnedState {
  step: OrchestrationStepState;
  left: number;
  top: number;
}

const NODE_W = DEFAULT_LAYOUT.nodeWidth;

const SlotDag: React.FC<SlotDagProps> = ({ slot, isLatest, nowMs }) => {
  const { phaseState } = slot;
  const layout = useMemo(
    () => layoutGraph(phaseState.steps),
    [phaseState.steps]
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const [offsetY, setOffsetY] = useState(0);
  const [drawH, setDrawH] = useState(layout.height);
  const [showHint, setShowHint] = useState(false);

  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [pinned, setPinned] = useState<PinnedState | null>(null);

  // Vertically center the graph in the pane; recompute on slot + resize.
  useLayoutEffect(() => {
    const recenter = () => {
      const pane = scrollRef.current;
      if (!pane) return;
      const paneH = pane.clientHeight || layout.height;
      const off = layout.height < paneH ? Math.round((paneH - layout.height) / 2) : 0;
      setOffsetY(off);
      setDrawH(Math.max(layout.height, paneH));
      setShowHint(
        pane.scrollWidth - pane.clientWidth - pane.scrollLeft > 6
      );
    };
    recenter();
    window.addEventListener("resize", recenter);
    return () => window.removeEventListener("resize", recenter);
  }, [layout]);

  // Reset interaction state when the selected slot changes.
  useEffect(() => {
    setPinned(null);
    setTooltip(null);
  }, [slot.slotUtc]);

  const onScroll = () => {
    const pane = scrollRef.current;
    if (!pane) return;
    setShowHint(pane.scrollWidth - pane.clientWidth - pane.scrollLeft > 6);
    if (pinned) setPinned(null);
  };

  const handleEnter = (step: OrchestrationStepState, e: React.MouseEvent) => {
    if (pinned) return;
    setTooltip({ step, x: e.clientX, y: e.clientY });
  };
  const handleMove = (e: React.MouseEvent) => {
    setTooltip((t) => (t ? { ...t, x: e.clientX, y: e.clientY } : t));
  };
  const handleLeave = () => setTooltip(null);

  const handleSelect = (step: OrchestrationStepState, el: HTMLElement) => {
    setTooltip(null);
    const rect = el.getBoundingClientRect();
    const popW = 330;
    const popH = 360;
    let left = rect.right + 12;
    if (left + popW > window.innerWidth) left = rect.left - popW - 12;
    if (left < 8) left = 8;
    let top = rect.top;
    if (top + popH > window.innerHeight) top = Math.max(8, window.innerHeight - popH - 8);
    if (top < 72) top = 72;
    setPinned({ step, left, top });
  };

  const big =
    phaseState.status === "in_progress"
      ? formatElapsed(phaseState.startedAtUtc, nowMs)
      : formatDuration(phaseState.durationMs);
  const bigLabel = phaseState.status === "in_progress" ? "elapsed" : "duration";

  const [execCopied, setExecCopied] = useState(false);

  return (
    <section className="flex min-w-0 flex-1 flex-col bg-gray-50 dark:bg-rp-black">
      {/* Selected-slot header */}
      <div className="flex flex-shrink-0 items-center gap-4 border-b border-gray-200 px-5 py-3 dark:border-rp-separator">
        <div>
          <div className="flex items-center gap-2 text-[15px] font-bold text-gray-900 dark:text-white">
            {phaseState.status === "in_progress" && (
              <span className="h-2 w-2 animate-pulse rounded-full bg-rp-amber" />
            )}
            {formatSlotLabel(slot.slotUtc)}
            {isLatest && (
              <span className="rounded border border-rp-amber/40 bg-rp-amber/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rp-amber">
                Latest
              </span>
            )}
          </div>
          <div className="font-mono text-[11px] text-gray-400 dark:text-gray-500">
            updated {formatSlotLabel(slot.updatedAtUtc)}
          </div>
        </div>
        <span
          className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${SLOT_PILL_CLASS[phaseState.status]}`}
        >
          {SLOT_STATUS_LABEL[phaseState.status]}
        </span>
        <div className="text-right">
          <div
            className={`font-mono text-2xl font-bold tabular-nums ${SLOT_BIG_CLASS[phaseState.status]}`}
          >
            {big}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
            {bigLabel}
          </div>
        </div>
        <div className="flex items-center gap-2 font-mono text-[12px] text-gray-400 dark:text-gray-500">
          <span className="truncate max-w-[220px]">
            {phaseState.orchestratorExecutionName}
          </span>
          <button
            type="button"
            aria-label="Copy execution name"
            onClick={() =>
              copyToClipboard(phaseState.orchestratorExecutionName)
                .then(() => {
                  setExecCopied(true);
                  window.setTimeout(() => setExecCopied(false), 1400);
                })
                .catch(() => undefined)
            }
            title={execCopied ? "Copied" : "Copy execution name"}
            className={`rounded border p-1 transition-colors ${
              execCopied
                ? "border-green-500 text-green-600 dark:text-green-400"
                : "border-gray-300 text-gray-500 hover:border-rp-amber hover:text-gray-900 dark:border-rp-separator dark:text-gray-400 dark:hover:text-white"
            }`}
          >
            {execCopied ? <Check size={12} /> : <Copy size={12} />}
          </button>
        </div>
        {/* legend */}
        <div className="ml-auto hidden items-center gap-3 text-[11px] text-gray-500 dark:text-gray-400 lg:flex">
          <span className="flex items-center gap-1.5"><StepStatusIcon status="succeeded" size={13} />succeeded</span>
          <span className="flex items-center gap-1.5"><StepStatusIcon status="running" size={13} />running</span>
          <span className="flex items-center gap-1.5"><StepStatusIcon status="failed" size={13} />failed</span>
          <span className="flex items-center gap-1.5"><StepStatusIcon status="pending" size={13} />pending</span>
          <span className="flex items-center gap-1.5"><StepStatusIcon status="skipped" size={13} />skipped</span>
        </div>
      </div>

      {/* Scrollable canvas */}
      <div className="relative flex-1 overflow-hidden">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          onClick={() => setPinned(null)}
          className="h-full overflow-auto"
        >
          <div className="relative" style={{ width: layout.width, height: drawH }}>
            {phaseState.steps.map((step) => {
              const p = layout.positions[step.id];
              if (!p) return null;
              return (
                <StepNode
                  key={step.id}
                  step={step}
                  x={p.x}
                  y={p.y + offsetY}
                  width={NODE_W}
                  pinned={pinned?.step.id === step.id}
                  onEnter={handleEnter}
                  onMove={handleMove}
                  onLeave={handleLeave}
                  onSelect={handleSelect}
                />
              );
            })}
          </div>
        </div>

        {/* right-edge scroll hint */}
        <div
          className={`pointer-events-none absolute bottom-0 right-0 top-0 flex w-28 items-center justify-end pr-4 transition-opacity duration-200 ${
            showHint ? "opacity-100" : "opacity-0"
          }`}
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(0,0,0,0.55))",
          }}
        >
          <span className="rounded-full border border-gray-300 bg-white px-2.5 py-1.5 text-[11px] text-gray-600 shadow dark:border-rp-separator dark:bg-rp-surface-raised dark:text-gray-300">
            scroll &rarr;
          </span>
        </div>
      </div>

      {/* hover tooltip */}
      {tooltip && !pinned && (
        <div
          className="pointer-events-none fixed z-[60] max-w-[250px] rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-[11.5px] text-gray-700 shadow-xl dark:border-rp-separator dark:bg-black dark:text-gray-200"
          style={{ left: tooltip.x + 14, top: tooltip.y + 14 }}
        >
          <div className="font-bold text-gray-900 dark:text-white">
            {tooltip.step.name}
          </div>
          <div className="font-mono text-[10.5px] text-gray-500 dark:text-gray-400">
            {tooltipLine(tooltip.step, nowMs)}
          </div>
        </div>
      )}

      {/* pinned popover */}
      {pinned && (
        <StepPopover
          step={pinned.step}
          executionNameFallback={phaseState.orchestratorExecutionName}
          left={pinned.left}
          top={pinned.top}
          onClose={() => setPinned(null)}
        />
      )}
    </section>
  );
};

function tooltipLine(step: OrchestrationStepState, _nowMs: number): string {
  switch (step.status) {
    case "succeeded":
      return `succeeded · ${formatDuration(step.durationMs)}`;
    case "running":
      return step.fanOut
        ? `running · fan-out ${step.fanOut.succeeded}/${step.fanOut.total}`
        : "running";
    case "failed":
      return `failed · ${formatDuration(step.durationMs)}`;
    case "skipped":
      return `skipped${step.skipReason ? ` · ${step.skipReason}` : ""}`;
    default:
      return `pending${step.estimatedDurationLabel ? ` · est ${step.estimatedDurationLabel}` : ""}`;
  }
}

export default SlotDag;
